import { useCustomToast } from '@elkaso-app/web-design';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import http from '../config/http-service';
import type { FamilyActivityPage } from './types';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

type TData = FamilyActivityPage;
type TError = AxiosError;
type TQueryKey = [string, number | undefined, number];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

// GET ums/v1/cms/master-product-families/:familyId/activity — paginated
// per-field activity feed for a family (and its variants). BE endpoint
// doesn't exist yet at time of writing; the Activity panel toasts once and
// stops retrying (retry: false) until it ships.
export function useGetFamilyActivityApi(familyId?: number, page = 1, options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const toast = useCustomToast();
  // Destructure to compose caller enabled with built-in guards (never replace)
  const { enabled: callerEnabled, ...restOptions } = options ?? {};

  const queryFn = async () => {
    try {
      const { data } = await http.get(`${masterProductFamiliesEndpoint}/${familyId}/activity`, {
        params: { page, limit: 20 },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([`${masterProductFamiliesEndpoint}/activity`, familyId, page], queryFn, {
    enabled: (callerEnabled ?? true) && isLoggedIn && familyId != null,
    retry: false,
    keepPreviousData: true,
    ...restOptions,
  });
}
